import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MessageCircle, Printer, Scissors, Shirt, Sparkles } from "lucide-react";
import { Reveal, EASE } from "@/components/Reveal";
import DesignCTA from "@/components/DesignCTA";
import {
  CONTACT,
  HOW_IT_WORKS,
  PRODUCTS,
  SERVICES,
  SPORTS,
  waLink,
  WHATSAPP_GENERIC,
  IMAGES,
} from "@/data/content";

const SERVICE_ICONS = { sublimation: Shirt, "cut-sew": Scissors, printing: Printer, embroidery: Sparkles };

function Hero() {
  return (
    <section className="relative min-h-[88vh] flex items-end overflow-hidden" data-testid="home-hero">
      <img src={IMAGES.heroHockey} alt="Ice hockey game in progress" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/45 to-navy/10" />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-navy/60 to-transparent" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-16 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="eyebrow !text-ice"
        >
          Top Player Sportswear — Sialkot, Pakistan
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          className="mt-4 font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02] max-w-3xl"
        >
          Custom sportswear. Built for your team.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.22, ease: EASE }}
          className="mt-5 max-w-xl text-base sm:text-lg text-slate-200 leading-relaxed"
        >
          Custom sportswear and teamwear manufactured around your team's design, colors and
          requirements.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.34, ease: EASE }}
          className="mt-8 flex flex-col sm:flex-row gap-4"
        >
          <Link to="/contact" data-testid="hero-cta-quote" className="btn-accent">
            Request a Quote <ArrowUpRight size={17} />
          </Link>
          <a
            href={waLink(WHATSAPP_GENERIC)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-whatsapp"
            className="btn-outline"
          >
            <MessageCircle size={17} />
            WhatsApp Us
          </a>
        </motion.div>
      </div>
    </section>
  );
}

const EMPHASIS = [
  "Custom teamwear",
  "Ice hockey specialization",
  "Multi-sport production",
  "Your designs & requirements",
];

function WhoWeAre() {
  return (
    <section className="py-14 md:py-20" data-testid="who-we-are">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-start">
        <Reveal>
          <p className="eyebrow mb-3">Who we are</p>
          <h2 className="h2">A sportswear manufacturer built around your design</h2>
        </Reveal>
        <div>
          <Reveal delay={0.1}>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              Top Player Sportswear is a sportswear manufacturer based in Sialkot, Pakistan,
              specializing in custom teamwear and sportswear. With a strong focus on ice
              hockey, we also manufacture apparel for a wide range of sports and athletic
              activities.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {EMPHASIS.map((e) => (
                <span key={e} className="border border-slate-200 bg-slate-50 rounded-md px-3.5 py-2 text-[13px] font-medium text-navy">
                  {e}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function WhatWeMake() {
  return (
    <section id="what-we-make" className="py-14 md:py-20 bg-slate-50 border-y border-slate-200/70 scroll-mt-24" data-testid="what-we-make">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-9">
          <Reveal><p className="eyebrow mb-3">Product range</p></Reveal>
          <Reveal delay={0.08}>
            <h2 className="h2">What we make</h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-3 text-base text-slate-600 leading-relaxed">
              One production partner for your team's full apparel program — every product
              made to your design, colors and specifications.
            </p>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p} delay={i * 0.04}>
              <div className="h-full bg-white rounded-lg border border-slate-200 px-6 py-6 flex items-baseline gap-4" data-testid={`product-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span className="font-display font-bold text-xl text-ice">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display font-semibold uppercase text-xl text-navy">{p}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ChooseYourSport() {
  const tiles = [...SPORTS, { id: "other", name: "Another Sport?", img: null, msg: WHATSAPP_GENERIC }];
  return (
    <section id="sports" className="py-14 md:py-20 scroll-mt-24" data-testid="choose-your-sport">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-9">
          <Reveal><p className="eyebrow mb-3">Start here</p></Reveal>
          <Reveal delay={0.08}>
            <h2 className="h2">Choose your sport</h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-3 text-base text-slate-600 leading-relaxed">
              Tell us what you play. Send us your design and requirements, and we'll take it
              from there.
            </p>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:auto-rows-[270px]">
          {tiles.map((s, i) => (
            <Reveal
              key={s.id}
              delay={i * 0.04}
              className={s.id === "hockey" ? "col-span-2 lg:row-span-2" : ""}
            >
              <a
                href={waLink(s.msg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`sport-tile-${s.id}`}
                className={`group relative block h-full min-h-52 overflow-hidden rounded-lg border border-slate-200 ${
                  s.img ? "" : "bg-icelight !border-ice/40"
                }`}
              >
                {s.img && (
                  <>
                    <img src={s.img} alt={`${s.name} teamwear`} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/15 to-transparent" />
                  </>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between gap-3">
                  <span className={`font-display font-semibold uppercase leading-tight ${s.id === "hockey" ? "text-2xl lg:text-3xl" : "text-xl"} ${s.img ? "text-white" : "text-navy"}`}>
                    {s.name}
                  </span>
                  <span className={`shrink-0 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider ${s.img ? "text-ice" : "text-icehover"} opacity-0 translate-y-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0`}>
                    <MessageCircle size={15} />
                    WhatsApp
                  </span>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ManufacturingServices() {
  return (
    <section className="py-14 md:py-20 bg-slate-50 border-y border-slate-200/70" data-testid="home-manufacturing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-9">
          <div className="max-w-2xl">
            <Reveal><p className="eyebrow mb-3">Manufacturing</p></Reveal>
            <Reveal delay={0.08}>
              <h2 className="h2">Custom manufacturing</h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-3 text-base text-slate-600 leading-relaxed">
                Four production services cover your team's apparel from fabric to finishing.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.18}>
            <Link to="/manufacturing" data-testid="home-manufacturing-link" className="link-accent">
              More about manufacturing <ArrowUpRight size={15} />
            </Link>
          </Reveal>
        </div>
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = SERVICE_ICONS[s.id];
            return (
              <Reveal key={s.id} delay={i * 0.06}>
                <div className="h-full bg-white rounded-lg border border-slate-200 p-7 hover:border-ice/60 hover:shadow-md transition-all duration-300" data-testid={`service-${s.id}`}>
                  <span className="inline-flex rounded-md bg-icelight p-3">
                    <Icon size={22} className="text-icehover" strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-5 font-display font-semibold uppercase text-xl text-navy">{s.name}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="py-14 md:py-20" data-testid="how-it-works">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-9">
          <Reveal><p className="eyebrow mb-3">The process</p></Reveal>
          <Reveal delay={0.08}>
            <h2 className="h2">How it works</h2>
          </Reveal>
        </div>
        <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-4">
          {HOW_IT_WORKS.map((s, i) => (
            <Reveal key={s.step} delay={i * 0.05}>
              <div className="h-full bg-white rounded-lg border border-slate-200 p-6" data-testid={`how-step-${s.step}`}>
                <span className="font-display font-bold text-3xl text-ice">{s.step}</span>
                <h3 className="mt-4 font-display font-semibold uppercase text-lg text-navy leading-tight">{s.name}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <WhoWeAre />
      <WhatWeMake />
      <ChooseYourSport />
      <ManufacturingServices />
      <HowItWorks />
      <DesignCTA />
    </>
  );
}
